// src/components/vehicles/ServicesList.js
import React, { useState, useEffect, useRef, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { Dialog } from "primereact/dialog";
import { InputNumber } from "primereact/inputnumber";
import { InputTextarea } from "primereact/inputtextarea";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";
import { api, formatDate, formatCurrency, toLocalDateString } from "../../utils/api";
import { AuthContext } from "../../context/AuthContext";
import "../../css/Fleet.css";

const ServicesList = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(1);
  const [vehicles, setVehicles] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [selectedServiceType, setSelectedServiceType] = useState(null);
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [form, setForm] = useState({ vehicleId: null, serviceTypeId: null, performedDate: null, validUntil: null, warningDaysBefore: 14, cost: null, notes: "" });
  const [summary, setSummary] = useState(null);
  const [allVehicles, setAllVehicles] = useState([]);
  const [vehicleSearch, setVehicleSearch] = useState("");
  const [renewInfo, setRenewInfo] = useState(null); // { isRenewable, hasExisting, latestRecord, renewDefaults }
  const [checkingRenew, setCheckingRenew] = useState(false);
  const toast = useRef(null);
  const navigate = useNavigate();
  const limit = 15;
  const { user } = useContext(AuthContext);
  const isAdmin = user?.role === "Administrator";

  useEffect(() => { loadDictionaries(); loadSummary(); }, []);
  useEffect(() => { loadServices(); }, [page, selectedVehicle, selectedServiceType, dateFrom, dateTo]);

  const loadDictionaries = async () => {
    try {
      const [vRes, stRes] = await Promise.all([
        api.get("/vehicles", { params: { limit: 1000, status: "ACTIVE" } }),
        api.get("/service-types"),
      ]);
      const vList = vRes.data?.data || [];
      setVehicles(vList.map((v) => ({ ...v, label: `${v.brand} ${v.model} (${v.registration_number})` })));
      setAllVehicles(vList);
      setServiceTypes(Array.isArray(stRes.data) ? stRes.data : []);
    } catch (err) { console.error(err); }
  };

  const loadSummary = async () => {
    try {
      const res = await api.get("/all-services-summary");
      setSummary(res.data);
    } catch (err) { console.error(err); }
  };

  const loadServices = async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (selectedVehicle) params.vehicleId = selectedVehicle;
      if (selectedServiceType) params.serviceTypeId = selectedServiceType;
      if (dateFrom) params.dateFrom = toLocalDateString(dateFrom);
      if (dateTo) params.dateTo = toLocalDateString(dateTo);
      const res = await api.get("/all-services", { params });
      setServices(Array.isArray(res.data?.data) ? res.data.data : []);
      setTotalRecords(res.data?.total || 0);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const openNew = () => {
    setForm({ vehicleId: null, serviceTypeId: null, performedDate: null, validUntil: null, warningDaysBefore: 14, cost: null, notes: "" });
    setRenewInfo(null);
    setDialogVisible(true);
  };

  const checkRenewable = async (vehicleId, serviceTypeId) => {
    if (!vehicleId || !serviceTypeId) { setRenewInfo(null); return; }
    setCheckingRenew(true);
    try {
      const res = await api.get(`/vehicles/${vehicleId}/service-check/${serviceTypeId}`);
      const info = res.data;
      setRenewInfo(info);
      if (info.isRenewable && info.hasExisting && info.renewDefaults) {
        setForm(prev => ({
          ...prev,
          performedDate: new Date(info.renewDefaults.performedDate),
          validUntil: new Date(info.renewDefaults.validUntil),
          warningDaysBefore: info.renewDefaults.warningDaysBefore,
        }));
      }
    } catch (err) { console.error(err); setRenewInfo(null); }
    setCheckingRenew(false);
  };

  const handleVehicleChange = (vehicleId) => {
    setForm(prev => ({ ...prev, vehicleId }));
    checkRenewable(vehicleId, form.serviceTypeId);
  };

  const handleServiceTypeChange = (serviceTypeId) => {
    setForm(prev => ({ ...prev, serviceTypeId }));
    checkRenewable(form.vehicleId, serviceTypeId);
  };

  const saveService = async () => {
    if (!form.vehicleId || !form.serviceTypeId || !form.performedDate) {
      toast.current.show({ severity: "warn", summary: "Uwaga", detail: "Pojazd, typ serwisu i data są wymagane" }); return;
    }
    try {
      // Jeśli to przedłużenie serwisu cyklicznego
      if (renewInfo?.isRenewable && renewInfo?.hasExisting && renewInfo?.latestRecord) {
        await api.post(`/services/${renewInfo.latestRecord.id}/renew`, {
          performedDate: form.performedDate,
          validUntil: form.validUntil,
          warningDaysBefore: form.warningDaysBefore,
          cost: form.cost,
          notes: form.notes,
        });
        toast.current.show({ severity: "success", summary: "Sukces", detail: "Serwis przedłużony" });
      } else {
        await api.post(`/vehicles/${form.vehicleId}/services`, {
          serviceTypeId: form.serviceTypeId, performedDate: form.performedDate,
          validUntil: form.validUntil, warningDaysBefore: form.warningDaysBefore,
          cost: form.cost, notes: form.notes,
        });
        toast.current.show({ severity: "success", summary: "Sukces", detail: "Serwis dodany" });
      }
      setDialogVisible(false); loadServices(); loadSummary();
    } catch (err) {
      console.error(err);
      const msg = err.response?.data?.message || "Nie udało się zapisać";
      toast.current.show({ severity: "error", summary: "Błąd", detail: msg });
    }
  };

  const deleteService = async (serviceId) => {
    if (!window.confirm("Usunąć ten serwis?")) return;
    try { await api.delete(`/services/${serviceId}`); toast.current.show({ severity: "success", summary: "Usunięto" }); loadServices(); loadSummary(); }
    catch (err) { console.error(err); }
  };

  const onPage = (e) => setPage(Math.floor(e.first / limit) + 1);

  const vehicleBodyTemplate = (row) => (
    <span className="fleet-vehicle-link" onClick={() => navigate(`/serwisy/pojazd/${row.vehicle_id}`)}>
      <i className="pi pi-car" style={{ fontSize: "0.8rem" }} />
      {row.brand} {row.model} ({row.registration_number})
    </span>
  );

  const getChangePercent = (current, previous) => {
    if (!previous || previous === 0) return current > 0 ? 100 : 0;
    return Math.round(((current - previous) / previous) * 100);
  };

  const openNewForVehicle = (vehicleId) => {
    setForm({ vehicleId, serviceTypeId: null, performedDate: null, validUntil: null, warningDaysBefore: 14, cost: null, notes: "" });
    setRenewInfo(null);
    setDialogVisible(true);
  };

  const openRenewFromTable = async (row) => {
    setForm({ vehicleId: row.vehicle_id, serviceTypeId: row.service_type_id, performedDate: null, validUntil: null, warningDaysBefore: 14, cost: null, notes: "" });
    setRenewInfo(null);
    setDialogVisible(true);
    // Automatycznie sprawdź i załaduj info o serwisie cyklicznym
    setCheckingRenew(true);
    try {
      const res = await api.get(`/vehicles/${row.vehicle_id}/service-check/${row.service_type_id}`);
      const info = res.data;
      setRenewInfo(info);
      if (info.isRenewable && info.hasExisting && info.renewDefaults) {
        setForm(prev => ({
          ...prev,
          performedDate: new Date(info.renewDefaults.performedDate),
          validUntil: new Date(info.renewDefaults.validUntil),
          warningDaysBefore: info.renewDefaults.warningDaysBefore,
        }));
      }
    } catch (err) { console.error(err); }
    setCheckingRenew(false);
  };

  const filteredVehicles = allVehicles.filter(v => {
    if (!vehicleSearch) return true;
    const s = vehicleSearch.toLowerCase();
    return (v.registration_number || '').toLowerCase().includes(s)
      || (v.brand || '').toLowerCase().includes(s)
      || (v.model || '').toLowerCase().includes(s)
      || (v.driver_operator || '').toLowerCase().includes(s)
      || (v.companyName || '').toLowerCase().includes(s);
  });

  const costChange = summary ? getChangePercent(summary.thisMonthCost, summary.lastMonthCost) : 0;
  const countChange = summary ? getChangePercent(summary.thisMonthCount, summary.lastMonthCount) : 0;

  return (
    <div className="fleet-page fleet-animate-in">
      <Toast ref={toast} />

      {/* Header */}
      <div className="fleet-page-header">
        <div>
          <h1><span className="fleet-header-icon">🛠️</span>Serwisy</h1>
          <p className="fleet-page-subtitle">Rejestr serwisów pojazdów ({totalRecords} łącznie)</p>
        </div>
        <Button label="Dodaj serwis" icon="pi pi-plus" className="fleet-btn-primary" onClick={openNew} />
      </div>

      {/* Summary Stat Cards */}
      {summary && (
        <div className="fleet-stats-grid fleet-stats-grid--5">
          <div className="fleet-stat-card fleet-stat-card--primary">
            <div className="fleet-stat-icon fleet-stat-icon--primary"><i className="pi pi-shield" /></div>
            <div className="fleet-stat-value fleet-stat-value--primary">{summary.totalServices}</div>
            <div className="fleet-stat-label">Łącznie serwisów</div>
            <div className="fleet-stat-sub">{summary.vehiclesWithServices} pojazdów</div>
          </div>

          {isAdmin && <div className="fleet-stat-card fleet-stat-card--danger">
            <div className="fleet-stat-icon fleet-stat-icon--danger"><i className="pi pi-wallet" /></div>
            <div className="fleet-stat-value fleet-stat-value--danger" style={{ fontSize: "1.2rem" }}>{formatCurrency(summary.totalCost)}</div>
            <div className="fleet-stat-label">Łączny koszt</div>
            <div className="fleet-stat-sub">śr. {formatCurrency(summary.avgCost)}/serwis</div>
          </div>}

          <div className="fleet-stat-card fleet-stat-card--purple">
            <div className="fleet-stat-icon fleet-stat-icon--purple"><i className="pi pi-calendar" /></div>
            <div className="fleet-stat-value fleet-stat-value--purple">{summary.thisMonthCount}</div>
            <div className="fleet-stat-label">Ten miesiąc</div>
            <div className={`fleet-stat-change ${countChange > 0 ? "fleet-stat-change--up" : countChange < 0 ? "fleet-stat-change--down" : ""}`}>
              {countChange > 0 ? "↑" : countChange < 0 ? "↓" : "—"} {Math.abs(countChange)}% vs poprz.
            </div>
          </div>

          {isAdmin && <div className="fleet-stat-card fleet-stat-card--warning">
            <div className="fleet-stat-icon fleet-stat-icon--warning"><i className="pi pi-money-bill" /></div>
            <div className="fleet-stat-value fleet-stat-value--warning" style={{ fontSize: "1.2rem" }}>{formatCurrency(summary.thisMonthCost)}</div>
            <div className="fleet-stat-label">Koszt (miesiąc)</div>
            <div className={`fleet-stat-change ${costChange > 0 ? "fleet-stat-change--up" : costChange < 0 ? "fleet-stat-change--down" : ""}`}>
              {costChange > 0 ? "↑" : costChange < 0 ? "↓" : "—"} {Math.abs(costChange)}% vs poprz.
            </div>
          </div>}

          <div className="fleet-stat-card fleet-stat-card--success">
            <div className="fleet-stat-icon fleet-stat-icon--success"><i className="pi pi-tag" /></div>
            <div className="fleet-stat-value fleet-stat-value--success" style={{ fontSize: "0.95rem" }}>{summary.topServiceType}</div>
            <div className="fleet-stat-label">Najczęstszy typ</div>
          </div>
        </div>
      )}

      {/* Vehicle Quick List */}
      {allVehicles.length > 0 && (
        <div className="fleet-card" style={{ marginBottom: "1.25rem" }}>
          <div className="fleet-card-header" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "0.5rem" }}>
            <h3 className="fleet-card-title"><i className="pi pi-car" /> Pojazdy</h3>
            <span className="p-input-icon-left" style={{ maxWidth: "280px" }}>
              <i className="pi pi-search" />
              <InputText value={vehicleSearch} onChange={(e) => setVehicleSearch(e.target.value)} placeholder="Szukaj pojazdu..." style={{ width: "100%" }} />
            </span>
          </div>
          <div className="fleet-card-body--flush fleet-table">
            <DataTable value={filteredVehicles} emptyMessage="Brak pojazdów" size="small" responsiveLayout="stack" breakpoint="600px" paginator rows={10} sortMode="multiple">
              <Column header="Nr rej." body={(row) => (
                <span className="fleet-vehicle-link" onClick={() => navigate(`/serwisy/pojazd/${row.id}`)}>
                  {row.registration_number}
                </span>
              )} field="registration_number" sortable />
              <Column header="Pojazd" body={(row) => `${row.brand} ${row.model}`} field="brand" sortable />
              <Column field="vehicleTypeName" header="Typ" sortable />
              <Column field="companyName" header="Firma" sortable />
              <Column field="driver_operator" header="Kierowca/Operator" body={(row) => row.driver_operator || "—"} sortable />
              <Column header="Akcje" body={(row) => (
                <div className="fleet-actions" style={{ gap: "0.25rem" }}>
                  <Button label="Serwis" icon="pi pi-plus" size="small" className="fleet-btn-primary" style={{ fontSize: "0.75rem", padding: "0.3rem 0.6rem" }} onClick={() => openNewForVehicle(row.id)} />
                </div>
              )} style={{ width: "160px" }} />
            </DataTable>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="fleet-filters">
        <Dropdown value={selectedVehicle} options={vehicles} onChange={(e) => { setSelectedVehicle(e.value); setPage(1); }} optionLabel="label" optionValue="id" placeholder="Pojazd" showClear filter style={{ minWidth: "260px" }} />
        <Dropdown value={selectedServiceType} options={serviceTypes} onChange={(e) => { setSelectedServiceType(e.value); setPage(1); }} optionLabel="name" optionValue="id" placeholder="Typ serwisu" showClear />
        <Calendar value={dateFrom} onChange={(e) => { setDateFrom(e.value); setPage(1); }} placeholder="Od daty" dateFormat="dd.mm.yy" showIcon showButtonBar />
        <Calendar value={dateTo} onChange={(e) => { setDateTo(e.value); setPage(1); }} placeholder="Do daty" dateFormat="dd.mm.yy" showIcon showButtonBar />
      </div>

      {/* Table */}
      <div className="fleet-card">
        <div className="fleet-card-body--flush fleet-table">
          <DataTable
            value={services} loading={loading}
            paginator rows={limit} totalRecords={totalRecords}
            lazy first={(page - 1) * limit} onPage={onPage}
            emptyMessage="Brak serwisów spełniających kryteria"
            responsiveLayout="stack" breakpoint="768px"
          >
            <Column header="Pojazd" body={vehicleBodyTemplate} />
            <Column field="serviceTypeName" header="Typ serwisu" />
            <Column header="Data wykonania" body={(row) => formatDate(row.performed_date)} sortable sortField="performed_date" />
            <Column header="Ważne do" body={(row) => formatDate(row.valid_until)} />
            <Column header="Koszt" body={(row) => (
              <span style={{ fontWeight: 600 }}>{formatCurrency(row.cost)}</span>
            )} />
            <Column field="notes" header="Notatki" style={{ maxWidth: "300px" }} />
            <Column field="createdByName" header="Dodał" />
            <Column header="Akcje" body={(row) => (
              <div className="fleet-actions" style={{ gap: "0.25rem" }}>
                {row.is_renewable && (
                  <Button icon="pi pi-refresh" rounded text size="small" severity="success" onClick={() => openRenewFromTable(row)} tooltip="Przedłuż" tooltipOptions={{ position: "top" }} />
                )}
                <Button icon="pi pi-trash" rounded text severity="danger" onClick={() => deleteService(row.id)} tooltip="Usuń" tooltipOptions={{ position: "top" }} />
              </div>
            )} style={{ width: "100px" }} />
          </DataTable>
        </div>
      </div>

      {/* Add Dialog */}
      <Dialog
        header={renewInfo?.isRenewable && renewInfo?.hasExisting ? "Przedłuż serwis" : "Nowy serwis"}
        visible={dialogVisible}
        style={{ width: "540px", maxWidth: "95vw" }}
        onHide={() => setDialogVisible(false)}
        className="fleet-dialog"
        footer={
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
            <Button label="Anuluj" icon="pi pi-times" className="p-button-text" onClick={() => setDialogVisible(false)} />
            <Button
              label={renewInfo?.isRenewable && renewInfo?.hasExisting ? "Przedłuż" : "Zapisz"}
              icon="pi pi-check"
              className="fleet-btn-primary"
              onClick={saveService}
              disabled={checkingRenew}
            />
          </div>
        }
      >
        <div className="p-fluid" style={{ display: "flex", flexDirection: "column", gap: "1.1rem" }}>
          <div className="fleet-form-field">
            <label>Pojazd *</label>
            <Dropdown value={form.vehicleId} options={vehicles} onChange={(e) => handleVehicleChange(e.value)} optionLabel="label" optionValue="id" placeholder="Wybierz pojazd" filter />
          </div>
          <div className="fleet-form-field">
            <label>Typ serwisu *</label>
            <Dropdown value={form.serviceTypeId} options={serviceTypes} onChange={(e) => handleServiceTypeChange(e.value)} optionLabel="name" optionValue="id" placeholder="Wybierz typ" />
          </div>

          {/* Renewable service info panel */}
          {checkingRenew && (
            <div style={{ padding: "0.75rem", background: "#f8fafc", borderRadius: "6px", textAlign: "center", color: "#64748b" }}>
              <i className="pi pi-spin pi-spinner" style={{ marginRight: "0.5rem" }} />Sprawdzanie...
            </div>
          )}
          {renewInfo?.isRenewable && renewInfo?.hasExisting && renewInfo?.latestRecord && !checkingRenew && (
            <div style={{ padding: "0.85rem", background: "#eff6ff", borderRadius: "8px", border: "1px solid #bfdbfe" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.5rem", fontWeight: 600, color: "#1e40af", fontSize: "0.9rem" }}>
                <i className="pi pi-info-circle" />
                Serwis cykliczny — przedłużenie
              </div>
              <div style={{ fontSize: "0.85rem", color: "#334155" }}>
                <div style={{ display: "flex", gap: "1.5rem", flexWrap: "wrap" }}>
                  <div>
                    <span style={{ color: "#64748b" }}>Ostatnio od: </span>
                    <strong>{formatDate(renewInfo.latestRecord.performedDate)}</strong>
                  </div>
                  <div>
                    <span style={{ color: "#64748b" }}>do: </span>
                    <strong>{formatDate(renewInfo.latestRecord.validUntil)}</strong>
                  </div>
                  {/* {renewInfo.latestRecord.cost != null && (
                    <div>
                      <span style={{ color: "#64748b" }}>Koszt: </span>
                      <strong>{formatCurrency(renewInfo.latestRecord.cost)}</strong>
                    </div>
                  )} */}
                </div>
                <p style={{ margin: "0.5rem 0 0", color: "#475569", fontSize: "0.82rem" }}>
                  Poniższe daty zostały wstępnie ustawione jako następujące po poprzednim serwisie. Możesz je zmienić.
                </p>
              </div>
            </div>
          )}
          {renewInfo?.isRenewable && !renewInfo?.hasExisting && !checkingRenew && (
            <div style={{ padding: "0.75rem", background: "#f0fdf4", borderRadius: "8px", border: "1px solid #bbf7d0", fontSize: "0.85rem", color: "#166534" }}>
              <i className="pi pi-check-circle" style={{ marginRight: "0.5rem" }} />
              Serwis cykliczny — pierwszy wpis dla tego pojazdu
            </div>
          )}

          <div className="fleet-form-grid">
            <div className="fleet-form-field">
              <label>{renewInfo?.isRenewable && renewInfo?.hasExisting ? "Nowa data od *" : "Data wykonania *"}</label>
              <Calendar value={form.performedDate} onChange={(e) => setForm({ ...form, performedDate: e.value })} dateFormat="dd.mm.yy" showIcon />
            </div>
            <div className="fleet-form-field">
              <label>{renewInfo?.isRenewable && renewInfo?.hasExisting ? "Nowa data do" : "Ważne do"}</label>
              <Calendar value={form.validUntil} onChange={(e) => setForm({ ...form, validUntil: e.value })} dateFormat="dd.mm.yy" showIcon />
            </div>
          </div>
          <div className="fleet-form-grid">
            <div className="fleet-form-field">
              <label>Ostrzeżenie (dni przed)</label>
              <InputNumber value={form.warningDaysBefore} onValueChange={(e) => setForm({ ...form, warningDaysBefore: e.value })} min={1} max={365} />
            </div>
            <div className="fleet-form-field">
              <label>Koszt (PLN)</label>
              <InputNumber value={form.cost} onValueChange={(e) => setForm({ ...form, cost: e.value })} mode="decimal" minFractionDigits={2} maxFractionDigits={2} />
            </div>
          </div>
          <div className="fleet-form-field">
            <label>Notatki</label>
            <InputTextarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} />
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default ServicesList;
